import { createContext, useContext, useState, useCallback, ReactNode } from "react";

interface ChatWidgetState {
  conversationId: string | null;
  isOpen: boolean;
  prefilledText: string | null;
  openChat: (conversationId: string, prefilledText?: string) => void;
  closeChat: () => void;
  toggleChat: () => void;
  consumePrefilledText: () => void;
}

const ChatWidgetContext = createContext<ChatWidgetState>({
  conversationId: null,
  isOpen: false,
  prefilledText: null,
  openChat: () => {},
  closeChat: () => {},
  toggleChat: () => {},
  consumePrefilledText: () => {},
});

export const ChatWidgetProvider = ({ children }: { children: ReactNode }) => {
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [prefilledText, setPrefilledText] = useState<string | null>(null);

  const openChat = useCallback((id: string, text?: string) => {
    setConversationId(id);
    setPrefilledText(text ?? null);
    setIsOpen(true);
  }, []);

  const closeChat = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggleChat = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const consumePrefilledText = useCallback(() => {
    setPrefilledText(null);
  }, []);

  return (
    <ChatWidgetContext.Provider value={{ conversationId, isOpen, prefilledText, openChat, closeChat, toggleChat, consumePrefilledText }}>
      {children}
    </ChatWidgetContext.Provider>
  );
};

export const useChatWidget = () => useContext(ChatWidgetContext);
